import { logStore } from "./logStore";
import type { LogCategory, LogContext, LogWriteOptions } from './types';

const LOG_SESSION_STORAGE_KEY = 'thinkmd_log_session_id';

interface StoreLike {
    getters?: Record<string, unknown>;
    state?: {
        v4?: {
            session?: {
                uuid?: string;
            };
        };
    };
}

function generateSessionId(): string {
    if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    return 'log-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function getEncounterSessionId(): string | undefined {
    const globalApp = (
        globalThis as typeof globalThis & { App?: { $store?: StoreLike } }
    ).App;
    const store = globalApp?.$store;
    if (!store) return undefined;

    const fromGetter = store.getters?.['v4/session/active'];
    if (typeof fromGetter === 'string' && fromGetter) {
        return fromGetter;
    }

    return store.state?.v4?.session?.uuid || undefined;
}

function getOrCreateLogSessionId(): string | undefined {
    try {
        const existing = globalThis.sessionStorage.getItem(LOG_SESSION_STORAGE_KEY);
        if (existing) {
            return existing;
        }

        const generated = generateSessionId();
        globalThis.sessionStorage.setItem(LOG_SESSION_STORAGE_KEY, generated);
        return generated;
    } catch {
        return undefined;
    }
}

function withSessionIdentifiers(options?: LogWriteOptions): LogWriteOptions {
    const enriched = { ...options };

    if (!enriched.sessionId) {
        enriched.sessionId = getEncounterSessionId();
    }

    if (!enriched.logSessionId) {
        enriched.logSessionId = getOrCreateLogSessionId();
    }

    return enriched;
}

export const logger = {
    error(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'ERROR',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    warn(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'WARN',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    info(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'INFO',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    debug(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'DEBUG',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    /**
     * Returns whether diagnostic mode is active.
     * @returns {boolean}
     */
    get diagnosticMode() {
        return logStore.diagnosticMode;
    },

    /**
     * Enables or disables diagnostic logging.
     * @param {boolean} enabled
     */
    setDiagnosticMode(enabled: boolean) {
        logStore.setDiagnosticMode(enabled);
    }
}
